<template>
  <article class="card-custom aev">
    <div class="aev-title">Desempeño por área</div>
    <div class="aev-sub">
      Score por sección de la encuesta ESAC ·
      {{ cat.anios.length > 1 ? `comparativo ${rango}` : rango }}
    </div>

    <!-- Advertencias de calidad del dato. Se emiten solas: desaparecen en
         cuanto el backend corrige el scoring o completa las secciones. -->
    <div v-if="cat.aniosFueraDeEscala?.length" class="aev-alert">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span>
        <strong>{{ cat.aniosFueraDeEscala.join(", ") }}</strong> quedó fuera de
        la comparación: sus scores por sección vienen en otra escala (máximo
        {{ maxFueraDeEscala }} contra 80-90 del resto), lo que produce un
        desplome y una recuperación que no ocurrieron. Corresponde al peso de
        sección sin normalizar en el scoring.
      </span>
    </div>

    <div v-if="cat.aniosIncompletos?.length" class="aev-note">
      <i class="bi bi-info-circle"></i>
      <span>
        {{ cat.aniosIncompletos.join(", ") }} no tiene medición en todas las
        secciones; las barras faltantes no son un cero.
      </span>
    </div>

    <!-- ---------- Gráfica de barras agrupadas ---------- -->
    <div class="aev-chart" @mouseleave="hover = null">
      <svg
        :viewBox="`0 0 ${W} ${H}`"
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Score por sección y año"
      >
        <g class="aev-grid">
          <line
            v-for="t in ticks"
            :key="'g' + t"
            :x1="ML"
            :x2="W - MR"
            :y1="y(t)"
            :y2="y(t)"
          />
        </g>

        <g class="aev-axis">
          <text
            v-for="t in ticks"
            :key="'t' + t"
            :x="ML - 8"
            :y="y(t) + 3.5"
            text-anchor="end"
          >
            {{ t }}
          </text>
          <line :x1="ML" :x2="W - MR" :y1="y(0)" :y2="y(0)" />
        </g>

        <template v-for="(fila, iSec) in cat.matriz" :key="fila.seccion">
          <!-- Barras: una por año dentro de la sección -->
          <rect
            v-for="(v, iAnio) in fila.valores"
            :key="fila.seccion + v.anio"
            class="aev-bar"
            :class="{ apagada: hover !== null && hover !== iSec }"
            :x="barX(iSec, iAnio)"
            :y="v.score == null ? y(0) : y(v.score)"
            :width="barW"
            :height="v.score == null ? 0 : y(0) - y(v.score)"
            :fill="colorAnio(iAnio)"
            rx="4"
          />
          <text
            class="aev-xlabel"
            :x="cx(iSec)"
            :y="H - MB + 18"
            text-anchor="middle"
          >
            {{ fila.seccion }}
          </text>
          <rect
            class="aev-hit"
            :x="ML + bandW * iSec"
            :y="MT"
            :width="bandW"
            :height="y(0) - MT"
            @mouseenter="hover = iSec"
          />
        </template>
      </svg>

      <div v-if="filaHover" class="aev-tip">
        <p class="tip-title">{{ filaHover.seccion }}</p>
        <p v-for="v in filaHover.valores" :key="v.anio">
          {{ v.anio }}:
          <strong>{{ v.score == null ? "sin medición" : v.score + "%" }}</strong>
          <span v-if="v.score != null" class="tip-dim">
            · {{ v.ejecuciones }} ejec. · {{ v.semaforo }}
          </span>
        </p>
      </div>
    </div>

    <div class="aev-legend">
      <span v-for="(a, i) in cat.anios" :key="a">
        <i class="lg" :style="{ background: colorAnio(i) }"></i> {{ a }}
      </span>
    </div>
  </article>
</template>

<script>
// Tonos de azul de la marca, del más claro (año más antiguo) al más oscuro.
const TONOS = ["#A8C4F0", "#1A5CC9", "#003CA5", "#002A7A"];

export default {
  name: "AreaEvolutionChart",

  props: {
    // Salida de construirCategorias()
    cat: { type: Object, required: true },
  },

  data() {
    return {
      hover: null,
      W: 820,
      H: 300,
      ML: 44,
      MR: 16,
      MT: 14,
      MB: 30,
    };
  },

  computed: {
    rango() {
      const a = this.cat.anios;
      if (!a.length) return "—";
      return a.length === 1 ? String(a[0]) : `${a[0]}–${a[a.length - 1]}`;
    },

    maxFueraDeEscala() {
      return this.cat.aniosFueraDeEscala?.length ? "27" : "";
    },

    ticks() {
      return [0, 25, 50, 75, 100];
    },

    bandW() {
      return (this.W - this.ML - this.MR) / (this.cat.matriz.length || 1);
    },

    barW() {
      const n = this.cat.anios.length || 1;
      return Math.min(44, (this.bandW * 0.66) / n);
    },

    filaHover() {
      return this.hover == null ? null : this.cat.matriz[this.hover] || null;
    },
  },

  methods: {
    // Escala fija 0-100: los scores por sección son porcentajes y el eje
    // completo evita exagerar diferencias pequeñas.
    y(v) {
      const alto = this.H - this.MB - this.MT;
      return this.H - this.MB - (Math.max(0, Math.min(100, v)) / 100) * alto;
    },

    cx(iSec) {
      return this.ML + this.bandW * (iSec + 0.5);
    },

    barX(iSec, iAnio) {
      const n = this.cat.anios.length || 1;
      const ancho = this.barW * n + 3 * (n - 1);
      return this.cx(iSec) - ancho / 2 + iAnio * (this.barW + 3);
    },

    colorAnio(i) {
      // El año más reciente siempre toma el tono más oscuro.
      const n = this.cat.anios.length || 1;
      return TONOS[Math.max(0, TONOS.length - n) + i] || TONOS[TONOS.length - 1];
    },
  },
};
</script>

<style scoped>
.aev-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--heading);
}

.aev-sub {
  font-size: 12px;
  color: var(--txt-dim);
  margin-bottom: 12px;
}

/* ---------- Avisos ---------- */
.aev-alert,
.aev-note {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 12px;
  line-height: 1.45;
  margin-bottom: 12px;
}

.aev-alert {
  background: rgba(255, 194, 75, 0.12);
  border: 1px solid rgba(255, 194, 75, 0.45);
  color: var(--txt);
}
.aev-alert i {
  color: var(--warn);
  margin-top: 1px;
}

.aev-note {
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--txt-dim);
}
.aev-note i {
  color: var(--txt-faint);
  margin-top: 1px;
}

/* ---------- Gráfica ---------- */
.aev-chart {
  position: relative;
}

.aev-chart svg {
  display: block;
}

.aev-grid line {
  stroke: var(--border);
  stroke-dasharray: 3 3;
}

.aev-axis line {
  stroke: var(--border-2);
}

.aev-axis text {
  font-size: 10.5px;
  fill: var(--txt-faint);
  font-variant-numeric: tabular-nums;
}

.aev-xlabel {
  font-size: 11.5px;
  font-weight: 600;
  fill: var(--txt-dim);
}

.aev-bar {
  transition: opacity 0.2s ease, height 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.aev-bar.apagada {
  opacity: 0.32;
}

.aev-hit {
  fill: transparent;
}

.aev-tip {
  position: absolute;
  top: 0;
  right: 0;
  background: var(--white);
  border: 1px solid var(--border-2);
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: var(--shadow-md);
  font-size: 12px;
  color: var(--txt-dim);
  pointer-events: none;
  min-width: 190px;
}
.aev-tip p {
  margin: 0 0 3px;
}
.aev-tip strong {
  color: var(--txt);
}
.tip-title {
  font-weight: 700;
  color: var(--txt);
  margin-bottom: 6px !important;
}
.tip-dim {
  color: var(--txt-faint);
  font-size: 11px;
}

/* ---------- Leyenda ---------- */
.aev-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 10px;
  font-size: 12px;
  color: var(--txt-dim);
}
.aev-legend .lg {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
}
</style>
